import {CommunityPostComment} from "../models/community/community-post-comment.model";
import {CommunityPost} from "../models/community/community-post.model";
import {CommunityPostsService} from "./services/community-posts.service";
import {MessagesService} from '../shared/services/messages.service';
import {noop} from 'rxjs';
import {CommunityMemberSettings} from '../models/community/community-member-settings.model';
import {User} from '../models/user.model';
import {CommunitySettings} from '../models/community/community-settings.model';
import {CommunityRoom} from '../models/community/community-room.model';
import {CommunityRoomUserAccess} from '../models/community/community-room-user-access.model';
import {Timestamp, arrayUnion, arrayRemove } from '@firebase/firestore';
import {doc, docData} from '@angular/fire/firestore';
import {first} from 'rxjs/operators';



export const ONE_WEEK_IN_MILLISECONDS = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds

export function prepPreviewComments(post: CommunityPost,comment: CommunityPostComment): CommunityPost {

  if (post.previewComments.length >= 2) {
    console.log('preview comments already has 2 comments');
    return null ;
  }

  post.previewComments.push(comment);

  return post;
}

export async function likePost(post: CommunityPost, userId: string, communityPostsService: CommunityPostsService) {

  const updatedPost = {
    ...post,
    likedBy: arrayUnion(userId),
    lastUpdatedAt: Timestamp.now(),
  }

  console.log("updating post with like", updatedPost);

  await communityPostsService.likeCommunityPost(updatedPost);

  return updatedPost;
}

export async function unlikePost(post: CommunityPost, userId: string, communityPostsService: CommunityPostsService) {

  const updatedPost = {
    ...post,
    likedBy: arrayRemove(userId),
    lastUpdatedAt: Timestamp.now(),
  }

  await communityPostsService.likeCommunityPost(updatedPost);

  return updatedPost;
}


export function sortPosts(posts: CommunityPost[], currentUser: any) {
  let sortedPosts = [...posts];

  sortedPosts.sort((a, b) => {
    const isNewA = checkIfIsNewPost(a, currentUser.communityMemberSettings);
    const isNewB = checkIfIsNewPost(b, currentUser.communityMemberSettings);

    if (a.pinned && !b.pinned) {
      return -1;
    } else if (!a.pinned && b.pinned) {
      return 1;
    } else if (isNewA && !isNewB) {
      return -1;
    } else if (!isNewA && isNewB) {
      return 1;
    } else {
      return b.createdAt.toMillis() - a.createdAt.toMillis();
    }
  });

  return sortedPosts;
}


export function checkIfIsNewPost(post: CommunityPost, communityMemberSettings: CommunityMemberSettings): boolean {

  const currentTime = new Date().getTime();
  const alreadyRead =
    communityMemberSettings?.postsWithUnreadCommentsIds?.length > 0
    ? communityMemberSettings?.postsWithUnreadCommentsIds?.includes(post.id)
    : false;

  if (post && post.createdAt && !alreadyRead) {

    const postTime = post.createdAt.toMillis();
    const timeDifference = currentTime - postTime;

    return timeDifference < ONE_WEEK_IN_MILLISECONDS; // if the post is created within 1 week
  }

  return false; // Default to false if post or createdAt is not available
}

export async function removePostIdFromCommunityMemberSettings(communityPostsService: CommunityPostsService, user: User, postId: string) {

  console.log('removing post id from community member settings');

  try {

    let communityMemberSettings = user?.communityMemberSettings;

    if (!communityMemberSettings) {
      return null;
    }

    const postsWithUnreadCommentsIds = communityMemberSettings?.postsWithUnreadCommentsIds;

    if (!postsWithUnreadCommentsIds?.includes(postId)) {
      console.log('postId not found in postsWithUnreadCommentsIds');
      return null;
    }

    const filteredPosts = postsWithUnreadCommentsIds.filter(id => id !== postId);

    console.log('######filteredPosts', filteredPosts);

    const changes = {
      communityMemberSettings: {
        ...communityMemberSettings,
        postsWithUnreadCommentsIds: filteredPosts ?? []
      }
    }

    await communityPostsService.updateCurrentUser(user.id, changes);

    return changes;

  } catch (err) {

    console.log('error updating community member settings', err);

  }

}

export async function addPostIdToCommunityMemberSettings(communityPostsService: CommunityPostsService, user: User, postId: string) {

  console.log('updating community member settings')

  try {

    let communityMemberSettings = user?.communityMemberSettings;

    // Create a new object for communityMemberSettings if it does not exist
    if (!communityMemberSettings) {
      communityMemberSettings = {} as CommunityMemberSettings;
    }

    const postsWithUnreadCommentsIds = communityMemberSettings?.postsWithUnreadCommentsIds ?? [];

    if (postsWithUnreadCommentsIds?.includes(postId)) { // post already read
      console.log('post already read');
      return null;
    }

    const changes = {
      communityMemberSettings: {
        ...communityMemberSettings,
        postsWithUnreadCommentsIds: [ ...postsWithUnreadCommentsIds, postId ]
      }
    }

    await communityPostsService.updateCurrentUser(user.id, changes);

    console.log('updated community member settings');

    return changes;

  } catch (err) {

    console.log('error updating community member settings', err);

  }
}

export function isUserAllowedInCommunity(communitySettings: CommunitySettings, isAdmin: boolean, currentUser: User) {

  const communityAccess = communitySettings?.communityAccess;

  if (communityAccess === 'free') {
    console.log('community access is free');
    return true;
  }

  if (isAdmin) {
    console.log('user is admin');
    return true;
  }

  if (communityAccess === 'invite-only' && currentUser?.isCommunityMember) {
    console.log('user is community member by invitation');
    return true;
  }

  const subscriptionPlan = currentUser?.communityMemberSettings?.communitySubscriptionPlan;

  if (
    communityAccess === 'community-subscription'
    && (subscriptionPlan === 'yearly' || subscriptionPlan === 'monthly')
    && currentUser?.communityMemberSettings?.communityStripeSubscriptionId
    && currentUser.isCommunityMember
  ) {
    console.log('user has subscribed to the community', subscriptionPlan);
    return true;
  }

  console.log('user is not allowed');

  return false;
}

export function isUserAllowedInRoom(room: CommunityRoom, isAdmin: boolean, roomUserAccess: CommunityRoomUserAccess[]) {

  if (room?.readAccess == 'everyone') {
    console.log('room can be accessed by everyone');
    return true;
  }

  if (isAdmin) {
    console.log('user is an admin');
    return true;
  }

  const roomSubscribed = roomUserAccess?.find(access => {
    return access.roomId == room?.id && room.readAccess == 'room-subscription' && access.communityRoomStripeSubscriptionId;
  });

  if (roomSubscribed) {
    console.log('user has subscribed to the room');
    return true;
  }

  const roomIdExist = roomUserAccess?.find(access => {
    return access?.roomId == room?.id && room.readAccess == 'invite-only'
  });

  if (roomIdExist) {
    console.log('user has access to the room');
    return true;
  }

  return false;
}

export function  hasWriteAccessForEveryoneOrIsAdmin(room: CommunityRoom, isAdmin: boolean) {

  if (!room) { // if room is not available
    return true;
  }

  return room?.writeAccess == 'everyone' || isAdmin;
}







